/**
 * PGF PlayerHeadshot - Headshot-style silhouette used across the product.
 *
 * Used on: Team HQ, Lineup, Player browser, Matchup Center, Draft Recap,
 * DraftBoard player pool (PlayerAvatar circular variant), ActiveNomination
 * (rounded-box hero variant). [TD-2 unification, v1.1 §3]
 *
 * IP COMPLIANCE v2: original SVG illustrations of generic head + shoulders.
 * No team colors, no jersey numbers, no identifying features.
 *
 * Variety: deterministic variant per player.id so the same player always shows
 * the same silhouette across the product. xs (28x28) uses a simplified
 * head-outline that ignores the variant for legibility.
 *
 * Exports:
 *   PlayerHeadshot        - rounded-box default for season pages + nomination hero
 *   PlayerAvatar          - circular variant for tiny rows (DraftBoard player pool)
 *   PlayerHeadshotCircle  - circular variant retaining the full HeadshotSVG (legacy)
 *   getPlayerVariant      - deterministic variant index from player.id
 */

const POSITION_COLORS = {
  QB:  "#E03030",
  RB:  "#00C853",
  WR:  "#007AFF",
  TE:  "#C97000",
  K:   "#707070",
  DEF: "#707070",
};

const SIZE_PX = { xs: 28, sm: 44, md: 64, lg: 128, xl: 180 };

/** Deterministic variant index from player.id */
export function getPlayerVariant(playerId) {
  if (!playerId) return 0;
  let h = 0;
  for (let i = 0; i < playerId.length; i++) {
    h = ((h << 5) - h) + playerId.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

/** Single headshot SVG with 3 head shapes × 4 hair/helmet × 2 ears = 24 variants */
function HeadshotSVG({ variant }) {
  const headShape = variant % 3;        // 0 oval / 1 round / 2 broad
  const hairStyle = Math.floor(variant / 3) % 4;  // 0 buzz / 1 fade / 2 helmet / 3 mohawk
  const earOffset = Math.floor(variant / 12) % 2; // 0 short / 1 long

  // Head ellipse
  const headProps = headShape === 0
    ? { cx: 50, cy: 38, rx: 22, ry: 26 }
    : headShape === 1
    ? { cx: 50, cy: 40, rx: 24, ry: 24 }
    : { cx: 50, cy: 38, rx: 26, ry: 25 };

  // Ears (peek)
  const earY = headProps.cy + (earOffset ? 4 : 0);

  return (
    <svg viewBox="0 0 100 100" fill="none" aria-hidden="true">
      {/* Neck */}
      <rect x="42" y="58" width="16" height="18" rx="3" fill="currentColor" opacity="0.55" />
      {/* Shoulders/torso bottom */}
      <path
        d="M 12 100 C 18 80, 30 75, 42 76 L 58 76 C 70 75, 82 80, 88 100 Z"
        fill="currentColor" opacity="0.85"
      />
      {/* Ears */}
      <ellipse cx={headProps.cx - headProps.rx + 2} cy={earY} rx="3" ry="5" fill="currentColor" opacity="0.70" />
      <ellipse cx={headProps.cx + headProps.rx - 2} cy={earY} rx="3" ry="5" fill="currentColor" opacity="0.70" />
      {/* Head */}
      <ellipse {...headProps} fill="currentColor" opacity="0.95" />
      {/* Hair / helmet variants */}
      {hairStyle === 0 && (
        // Buzz cut - thin top
        <path
          d={`M ${headProps.cx - headProps.rx + 4} ${headProps.cy - headProps.ry + 6}
              Q ${headProps.cx} ${headProps.cy - headProps.ry - 2}
              ${headProps.cx + headProps.rx - 4} ${headProps.cy - headProps.ry + 6}
              L ${headProps.cx + headProps.rx - 4} ${headProps.cy - headProps.ry + 12}
              L ${headProps.cx - headProps.rx + 4} ${headProps.cy - headProps.ry + 12} Z`}
          fill="currentColor" opacity="0.55"
        />
      )}
      {hairStyle === 1 && (
        // Fade - taller side
        <path
          d={`M ${headProps.cx - headProps.rx + 2} ${headProps.cy - 2}
              L ${headProps.cx - headProps.rx + 2} ${headProps.cy - headProps.ry + 4}
              Q ${headProps.cx} ${headProps.cy - headProps.ry - 4}
              ${headProps.cx + headProps.rx - 2} ${headProps.cy - headProps.ry + 4}
              L ${headProps.cx + headProps.rx - 2} ${headProps.cy - 2} Z`}
          fill="currentColor" opacity="0.45"
        />
      )}
      {hairStyle === 2 && (
        // Helmet stripe
        <>
          <path
            d={`M ${headProps.cx - headProps.rx} ${headProps.cy - 2}
                Q ${headProps.cx} ${headProps.cy - headProps.ry - 6}
                ${headProps.cx + headProps.rx} ${headProps.cy - 2} Z`}
            fill="currentColor" opacity="0.55"
          />
          <line
            x1={headProps.cx - 1}
            y1={headProps.cy - headProps.ry}
            x2={headProps.cx - 1}
            y2={headProps.cy - 2}
            stroke="currentColor" strokeWidth="2" opacity="0.85"
          />
        </>
      )}
      {hairStyle === 3 && (
        // Mohawk - narrow strip
        <rect
          x={headProps.cx - 4}
          y={headProps.cy - headProps.ry - 4}
          width="8"
          height={headProps.ry / 2 + 8}
          rx="2"
          fill="currentColor" opacity="0.55"
        />
      )}
    </svg>
  );
}

/**
 * PlayerHeadshot - rounded-box default for season pages.
 *
 * @param {object} props
 * @param {string} props.playerId  - used for variant determinism
 * @param {string} props.position  - QB | RB | WR | TE | K | DEF
 * @param {"xs"|"sm"|"md"|"lg"|"xl"} props.size
 * @param {string} [props.className]
 */
export function PlayerHeadshot({ playerId, position, size = "md", className = "" }) {
  const px = SIZE_PX[size] || SIZE_PX.md;
  const color = POSITION_COLORS[position] || POSITION_COLORS.DEF;
  const variant = getPlayerVariant(playerId);
  const tint = `${color}14`;
  const border = `${color}40`;

  return (
    <div
      className={`flex items-center justify-center rounded-2xl overflow-hidden ${className}`}
      style={{
        width: px, height: px,
        background: tint,
        border: `1.5px solid ${border}`,
      }}
      data-testid="player-headshot"
      aria-label={`${position || "Player"} silhouette`}
    >
      <div className="w-[70%] h-[70%]" style={{ color }}>
        <HeadshotSVG variant={variant} />
      </div>
    </div>
  );
}

/**
 * Minimal head-outline SVG for tiny avatars (28x28).
 * Per Post-Phase-1 Cleanup Spec v1.1 §3.2 [A5]: at 28x28 the detailed HeadshotSVG
 * doesn't read. We use head + neck + shoulders only, no hair/helmet/ears.
 */
function MinimalAvatarSVG() {
  return (
    <svg viewBox="0 0 100 100" fill="none" aria-hidden="true">
      {/* Shoulders */}
      <path
        d="M 8 100 C 14 78, 28 72, 42 74 L 58 74 C 72 72, 86 78, 92 100 Z"
        fill="currentColor" opacity="0.85"
      />
      {/* Neck */}
      <rect x="42" y="56" width="16" height="20" rx="3" fill="currentColor" opacity="0.55" />
      {/* Head - simple round ellipse, no interior detail */}
      <ellipse cx="50" cy="36" rx="24" ry="26" fill="currentColor" opacity="0.95" />
    </svg>
  );
}

/**
 * PlayerHeadshotCircle - circular variant for backward compat (uses full HeadshotSVG).
 * Kept for any existing season callsites; new code should prefer PlayerAvatar.
 */
export function PlayerHeadshotCircle({ playerId, position, size = "sm", className = "" }) {
  const px = SIZE_PX[size] || SIZE_PX.sm;
  const color = POSITION_COLORS[position] || POSITION_COLORS.DEF;
  const variant = getPlayerVariant(playerId);

  return (
    <div
      className={`flex items-center justify-center rounded-full overflow-hidden ${className}`}
      style={{
        width: px, height: px,
        background: `${color}14`,
        border: `1.5px solid ${color}40`,
      }}
      data-testid="player-headshot-circle"
    >
      <div className="w-[70%] h-[70%]" style={{ color }}>
        <HeadshotSVG variant={variant} />
      </div>
    </div>
  );
}

/**
 * PlayerAvatar - circular avatar for player-pool lists and tiny displays.
 * Replaces the legacy circular avatar export from the deleted full-body silhouette
 * file (TD-2, v1.1 §3.2).
 *
 * Sizes: xs (28x28) and sm (44x44). At xs we use MinimalAvatarSVG (head-only outline)
 * because the detailed HeadshotSVG with hair/helmet variants doesn't read at 28x28.
 *
 * Implementation note [A5]: the simplified head-outline approach is preferred over
 * a colored-circle monogram because the position color tint already encodes position
 * identity; the monogram fallback remains available if visual review demands it.
 *
 * @param {string} [props.playerId]  - optional; used for variant determinism at sm size
 * @param {string} props.position    - QB | RB | WR | TE | K | DEF
 * @param {"xs"|"sm"} [props.size="sm"]
 * @param {string} [props.className]
 */
export function PlayerAvatar({ playerId, position, size = "sm", className = "" }) {
  const px = size === "xs" ? SIZE_PX.xs : SIZE_PX.sm;
  const color = POSITION_COLORS[position] || POSITION_COLORS.DEF;
  const useMinimal = size === "xs";
  const innerPct = size === "xs" ? "70%" : "70%";

  return (
    <div
      className={`rounded-full flex items-center justify-center shrink-0 relative overflow-hidden ${className}`}
      style={{
        width: px,
        height: px,
        background: `${color}14`,
        border: `1.5px solid ${color}40`,
      }}
      data-testid="player-avatar"
      aria-label={`${position || "Player"} avatar`}
    >
      <div style={{ color, width: innerPct, height: innerPct }}>
        {useMinimal
          ? <MinimalAvatarSVG />
          : <HeadshotSVG variant={getPlayerVariant(playerId)} />}
      </div>
    </div>
  );
}

export default PlayerHeadshot;
